import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Title,
  Stack,
  Box,
  useMantineColorScheme,
  SimpleGrid,
  Paper,
  Group,
  Button,
  Loader,
  Modal,
  Menu,
} from '@mantine/core';
import {
  IconRun,
  IconSettings,
  IconUsers,
  IconTrophy,
  IconClock,
  IconArrowRight,
  IconPlus,
  IconTrash,
  IconDotsVertical,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { authService } from '../services/authService';
import examenService from '../services/examenService';
import { ExamenEnProgreso } from '../types/examen.types';
import { useQuery } from '@tanstack/react-query';
import { subscriptionStatusQuery } from '../queries/appQueries';

const SimuladorPage: React.FC = () => {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();

  const [cargandoProgreso, setCargandoProgreso] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [opcionSeleccionada, setOpcionSeleccionada] = useState<any>(null);
  // Bloquea las acciones mientras se abandona/elimina un examen en progreso
  const [procesandoAccion, setProcesandoAccion] = useState(false);
  // Suscripción cacheada con React Query (compartida con el Sidebar). null = cargando
  const subQ = useQuery(subscriptionStatusQuery());
  const esUsuarioGratuito: boolean | null =
    subQ.data === undefined ? null : (subQ.data?.isFreeplan === true || !subQ.data?.subscriptionId);
  const [modalMejorarPlan, setModalMejorarPlan] = useState(false);

  // Estados para exámenes en progreso de cada tipo
  const [examenRapido, setExamenRapido] = useState<ExamenEnProgreso | null>(null);
  const [examenFiltrado, setExamenFiltrado] = useState<ExamenEnProgreso | null>(null);
  const [examenENARM, setExamenENARM] = useState<ExamenEnProgreso | null>(null);
  const [examenMulti, setExamenMulti] = useState<ExamenEnProgreso | null>(null);

  // Cargar tipo de plan y verificar exámenes en progreso al montar el componente
  useEffect(() => {
    const cargarDatos = async () => {
      console.log('🔎 [SIMULADOR_LOAD] ====== INICIO ======');
      console.log('🎯 [SIMULADOR_LOAD] SimuladorPage: Verificando tipo de plan y exámenes en progreso');

      const user = authService.getCurrentUserFromStorage();
      if (!user) {
        console.log('⚠️ [SIMULADOR_LOAD] No hay usuario autenticado');
        setCargandoProgreso(false);
        console.log('🏁 [SIMULADOR_LOAD] ====== FIN (Sin usuario) ======');
        return;
      }

      console.log('✅ [SIMULADOR_LOAD] Usuario autenticado:', user.id, '-', user.username);

      try {
        // Verificar exámenes en progreso de cada tipo (en paralelo, sin borrar nada)
        console.log('🔍 [SIMULADOR] Verificando exámenes en progreso por tipo...');
        const [rapido, filtrado, enarm, multi] = await Promise.all([
          examenService.verificarExamenEnProgresoPorTipo(user.id, 'RAPIDO').catch(() => null),
          examenService.verificarExamenEnProgresoPorTipo(user.id, 'FILTRADO').catch(() => null),
          examenService.verificarExamenEnProgresoPorTipo(user.id, 'ENARM').catch(() => null),
          examenService.verificarExamenEnProgresoPorTipo(user.id, 'MULTIJUGADOR').catch(() => null),
        ]);

        setExamenRapido(rapido);
        setExamenFiltrado(filtrado);
        setExamenENARM(enarm);
        setExamenMulti(multi);

        console.log('📊 [SIMULADOR] Exámenes en progreso encontrados:');
        console.log('   - RAPIDO:', rapido ? `ID ${rapido.intentoId}` : 'ninguno');
        console.log('   - FILTRADO:', filtrado ? `ID ${filtrado.intentoId}` : 'ninguno');
        console.log('   - ENARM:', enarm ? `ID ${enarm.intentoId}` : 'ninguno');
        console.log('   - MULTIJUGADOR:', multi ? `ID ${multi.intentoId}` : 'ninguno');

        console.log('🎉 [SIMULADOR_LOAD] ====== ÉXITO ======');
      } catch (error: any) {
        console.error('❌ [SIMULADOR_LOAD] ERROR:');
        console.error('   - Tipo:', error.constructor.name);
        console.error('   - Mensaje:', error.message);
        console.log('🚨 [SIMULADOR_LOAD] ====== ERROR ======');
      } finally {
        setCargandoProgreso(false);
      }
    };

    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // SIN DEPENDENCIAS para evitar loops

  const formatearTiempo = (segundos: number): string => {
    const horas = Math.floor(segundos / 3600);
    const mins = Math.floor((segundos % 3600) / 60);
    const secs = segundos % 60;

    if (horas > 0) {
      return `${horas}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const continuarExamen = (examen: ExamenEnProgreso) => {
    console.log('🔎 [SIMULADOR_CONTINUAR] Continuando examen tipo:', examen.tipoExamen);

    // Redirigir según el tipo de examen
    if (examen.tipoExamen === 'RAPIDO') {
      navigate('/estudiante/simulador/rapido', {
        state: {
          continuandoExamen: true,
          continuarDirecto: true, // Entrar directo al examen, sin volver a mostrar el modal
          examenEnProgreso: examen
        }
      });
    } else if (examen.tipoExamen === 'ENARM') {
      navigate('/estudiante/simulador/completo', {
        state: {
          continuandoExamen: true,
          continuarDirecto: true, // Entrar directo al examen, sin volver a mostrar el modal
          examenEnProgreso: examen
        }
      });
    } else if (examen.tipoExamen === 'FILTRADO') {
      navigate(`/estudiante/examen-filtrado/${examen.intentoId}`, {
        state: {
          continuandoExamen: true,
          continuarDirecto: true, // Entrar directo al examen, sin volver a mostrar el modal
          examenEnProgreso: examen
        }
      });
    } else if (examen.tipoExamen === 'MULTIJUGADOR') {
      navigate('/estudiante/multijugador');
    }
  };

  // Limpia el examen en progreso del estado local según la opción
  const limpiarExamenLocal = (opcionId: string) => {
    if (opcionId === 'express') setExamenRapido(null);
    else if (opcionId === 'filtros') setExamenFiltrado(null);
    else if (opcionId === 'completo') setExamenENARM(null);
    else if (opcionId === 'multiplayer') setExamenMulti(null);
  };

  // Elimina (abandona) el examen en progreso y cierra el panel; la card vuelve a su estado normal
  const eliminarExamenEnProgreso = async (opcionId: string, examen: ExamenEnProgreso) => {
    setProcesandoAccion(true);
    try {
      await examenService.abandonarExamen(examen.intentoId);
      limpiarExamenLocal(opcionId);
    } catch (error) {
      console.error('❌ [SIMULADOR] Error al eliminar examen en progreso:', error);
    } finally {
      setProcesandoAccion(false);
    }
  };

  // Abandona el examen en progreso y arranca uno nuevo del mismo tipo
  const empezarNuevoExamen = async (opcion: any, examen: ExamenEnProgreso) => {
    setProcesandoAccion(true);
    try {
      await examenService.abandonarExamen(examen.intentoId);
      limpiarExamenLocal(opcion.id);
      navigate(opcion.route);
    } catch (error) {
      console.error('❌ [SIMULADOR] Error al iniciar un examen nuevo:', error);
      setProcesandoAccion(false);
    }
  };

  // Configuración de las 4 opciones principales
  const opcionesSimulador = [
    {
      id: 'express',
      title: 'Quiz Express',
      description: '10 preguntas al azar',
      status: null,
      icon: IconRun,
      color: '#bfdbfe', // azul pastel suave
      bgColor: colorScheme === 'dark' ? 'rgba(191, 219, 254, 0.12)' : 'rgba(191, 219, 254, 0.08)',
      borderColor: 'rgba(191, 219, 254, 0.4)',
      route: '/estudiante/simulador/rapido',
      disabled: false, // Siempre disponible
      requiresPremium: false
    },
    {
      id: 'filtros',
      title: 'Arma tu Examen',
      description: 'Tú eliges las especialidades\ny la cantidad de preguntas',
      status: esUsuarioGratuito ? 'Premium' : null,
      icon: IconSettings,
      color: '#fed7aa', // durazno pastel suave
      bgColor: colorScheme === 'dark' ? 'rgba(254, 215, 170, 0.12)' : 'rgba(254, 215, 170, 0.08)',
      borderColor: 'rgba(254, 215, 170, 0.4)',
      route: '/estudiante/simulador/filtros',
      disabled: esUsuarioGratuito === true, // Bloqueado para gratuitos
      requiresPremium: true
    },
    {
      id: 'multiplayer',
      title: 'Multiplayer',
      description: 'Únete a un examen\ncompartido y compite por\n1er lugar',
      status: esUsuarioGratuito ? 'Premium' : null,
      icon: IconUsers,
      color: '#d9f99d', // verde lima pastel suave
      bgColor: colorScheme === 'dark' ? 'rgba(217, 249, 157, 0.12)' : 'rgba(217, 249, 157, 0.08)',
      borderColor: 'rgba(217, 249, 157, 0.4)',
      route: '/multijugador',
      disabled: esUsuarioGratuito === true, // Solo bloqueado para usuarios gratuitos
      requiresPremium: true
    },
    {
      id: 'completo',
      title: 'Completo',
      description: 'Simula la experiencia de\nun ENARM completo',
      status: esUsuarioGratuito ? 'Premium' : null,
      icon: IconTrophy,
      color: '#fecaca', // coral pastel suave
      bgColor: colorScheme === 'dark' ? 'rgba(254, 202, 202, 0.12)' : 'rgba(254, 202, 202, 0.08)',
      borderColor: 'rgba(254, 202, 202, 0.4)',
      route: '/estudiante/simulador/completo',
      disabled: esUsuarioGratuito === true, // Bloqueado para gratuitos
      requiresPremium: true
    }
  ];


  const handleOpcionClick = (opcion: any) => {
    // Prevenir clics mientras se carga el estado del usuario
    if (esUsuarioGratuito === null) {
      return;
    }
    
    // Si está deshabilitado y requiere premium, mostrar modal de mejora
    if (opcion.disabled && opcion.requiresPremium && esUsuarioGratuito) {
      setModalMejorarPlan(true);
      return;
    }
    
    // No permite navegar si está deshabilitado por otra razón
    if (opcion.disabled) {
      return;
    }
    
    // Si es "Arma tu Examen", ir directo sin modal
    if (opcion.id === 'filtros') {
      navigate(opcion.route);
      return;
    }
    
    // Para otros, abrir modal de confirmación
    setOpcionSeleccionada(opcion);
    setModalAbierto(true);
  };

  const confirmarIniciarExamen = () => {
    if (opcionSeleccionada) {
      navigate(opcionSeleccionada.route);
      setModalAbierto(false);
      setOpcionSeleccionada(null);
    }
  };

  return (
    <>
      {/* Modal de mejora de plan */}
      <Modal
        opened={modalMejorarPlan}
        onClose={() => setModalMejorarPlan(false)}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{
          content: {
            borderRadius: '16px',
          },
        }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Función Premium</Title>
            <Text size="sm" c="dimmed">
              Esta funcionalidad está disponible solo para usuarios con plan Premium.
            </Text>
            <Text size="sm" c="dimmed" mt="sm">
              ¿Deseas mejorar tu plan para acceder a todas las funciones?
            </Text>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setModalMejorarPlan(false)}
              radius="md"
            >
              Cancelar
            </Button>
            <Button
              variant="filled"
              style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
              }}
              onClick={() => {
                setModalMejorarPlan(false);
                navigate('/mejorarplan');
              }}
              radius="md"
            >
              Ver Planes
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal de confirmación */}
      <Modal
        opened={modalAbierto}
        onClose={() => setModalAbierto(false)}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{
          content: {
            borderRadius: '16px',
          },
        }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Iniciar Examen</Title>
            {opcionSeleccionada && (
              <Stack gap="sm">
                <Text size="sm" fw={600}>{opcionSeleccionada.title}</Text>
                <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
                  {opcionSeleccionada.description}
                </Text>
                {opcionSeleccionada.id === 'express' && (
                  <Text size="xs" c="dimmed">
                    Este examen consta de 10 preguntas aleatorias de diferentes especialidades.
                  </Text>
                )}
                {opcionSeleccionada.id === 'filtros' && (
                  <Text size="xs" c="dimmed">
                    Podrás personalizar el número de preguntas y seleccionar las especialidades que desees.
                  </Text>
                )}
                {opcionSeleccionada.id === 'completo' && (
                  <Stack gap="xs" mt="xs">
                    <Group gap={6} align="center">
                      <IconAlertTriangle size={14} color="var(--mantine-color-yellow-7)" />
                      <Text size="xs" fw={600} c="dimmed">
                        Información Importante
                      </Text>
                    </Group>
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed">
                        • Este simulacro contiene 280 preguntas (reactivos)
                      </Text>
                      <Text size="xs" c="dimmed">
                        • Tiempo límite: 4 horas (240 minutos)
                      </Text>
                      <Text size="xs" c="yellow" fw={500}>
                        • Casos clínicos próximamente
                      </Text>
                      <Text size="xs" c="dimmed">
                        • Puedes pausar el examen y retomarlo después
                      </Text>
                      <Text size="xs" c="dimmed">
                        • Asegúrate de estar en un lugar tranquilo
                      </Text>
                    </Stack>
                  </Stack>
                )}
                {opcionSeleccionada.id === 'multiplayer' && (
                  <Text size="xs" c="dimmed">
                    Podrás crear o unirte a una sala y competir con otros estudiantes en tiempo real.
                  </Text>
                )}
              </Stack>
            )}
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setModalAbierto(false)}
              radius="md"
            >
              Cancelar
            </Button>
            <Button
              variant="filled"
              color="dark"
              onClick={confirmarIniciarExamen}
              radius="md"
            >
              {opcionSeleccionada?.id === 'completo' ? 'Iniciar simulacro' : 'Continuar'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Box
        style={{
          padding: '16px 24px',
          minHeight: '100vh',
          overflow: 'hidden',
          overflowY: 'auto',
          backgroundColor: colorScheme === 'dark' 
            ? 'rgba(15, 23, 42, 0.5)' 
            : 'rgba(247, 243, 238, 0.3)',
        }}
      >
        {/* Header */}
      <Box mb="sm" ta="left">
        <Title
          order={2}
          size="1.3rem"
          fw={600}
          ta="left"
          style={{
            color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
            fontFamily: 'Space Grotesk, Inter, sans-serif',
            marginBottom: '4px',
          }}
        >
          Elige tu cuestionario
        </Title>
        <Text
          size="xs"
          ta="left"
          style={{
            color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Selecciona el tipo de examen que mejor se adapte a tu estudio
        </Text>
      </Box>


      {/* Opciones principales - 4 tarjetas */}
      <SimpleGrid
        cols={{ base: 1, sm: 2, lg: 4 }}
        spacing="xl"
        mb="4rem"
      >
        {opcionesSimulador.map((opcion) => {
          const IconComponent = opcion.icon;

          // Determinar qué examen en progreso corresponde a esta opción
          let examenEnProgreso: ExamenEnProgreso | null = null;
          if (opcion.id === 'express') examenEnProgreso = examenRapido;
          else if (opcion.id === 'filtros') examenEnProgreso = examenFiltrado;
          else if (opcion.id === 'completo') examenEnProgreso = examenENARM;
          else if (opcion.id === 'multiplayer') examenEnProgreso = examenMulti;

          // Solo mostrar badge si NO está 100% completado
          const tieneProgresoActivo = examenEnProgreso &&
            (examenEnProgreso.preguntaActual + 1 < examenEnProgreso.totalPreguntas);

          return (
            <Stack key={opcion.id} gap="sm">
            <Paper
              key={opcion.id}
              onClick={() => {
                // Con examen en progreso las acciones viven en el menú de tres puntos;
                // el clic en la card no inicia nada.
                if (tieneProgresoActivo && !opcion.disabled && esUsuarioGratuito !== null) {
                  return;
                }
                handleOpcionClick(opcion);
              }}
              style={{
                backgroundColor: opcion.bgColor,
                border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(237, 231, 221, 0.8)'}`,
                borderRadius: 'var(--mantine-radius-md)',
                padding: '2rem',
                height: '320px',
                cursor: (opcion.disabled || esUsuarioGratuito === null) ? 'not-allowed' : 'pointer',
                opacity: (opcion.disabled || esUsuarioGratuito === null) ? 0.7 : 1,
                transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                position: 'relative',
                overflow: 'hidden',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                display: 'flex',
                flexDirection: 'column',
              }}
              onMouseEnter={(e) => {
                if (!opcion.disabled) {
                  e.currentTarget.style.boxShadow = `0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px ${opcion.color}20`;
                }
              }}
              onMouseLeave={(e) => {
                if (!opcion.disabled) {
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >

              <Stack align="center" gap="md" style={{ height: '100%', justifyContent: 'flex-start', paddingTop: '1rem' }}>
                {/* Icon: solo el ícono grande en negro, sin contenedor */}
                <IconComponent size={64} style={{ color: '#1e293b', fill: opcion.color, marginBottom: '0.5rem' }} />

                {/* Content */}
                <Stack align="center" gap="sm">
                  <Title
                    order={3}
                    size="1.25rem"
                    fw={600}
                    ta="center"
                    style={{
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                      fontFamily: 'Space Grotesk, Inter, sans-serif',
                    }}
                  >
                    {opcion.title}
                  </Title>
                  <Text
                    size="sm"
                    ta="center"
                    style={{
                      color: '#334155',
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: 1.4,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {opcion.description}
                  </Text>

                  {/* Examen en progreso: píldora con hover que abre un menú de opciones */}
                  {tieneProgresoActivo && examenEnProgreso && (
                    <Menu
                      shadow="md"
                      width={232}
                      position="bottom"
                      radius="md"
                      withinPortal
                      styles={{
                        dropdown: {
                          border: 'none',
                          backgroundColor: '#ebe8e3',
                          padding: '6px',
                          '--menu-item-color': '#2d2a26',
                          '--menu-item-hover': 'rgba(0, 0, 0, 0.06)',
                        },
                        label: {
                          fontSize: '11px',
                          color: '#6b6256',
                        },
                        item: {
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: 500,
                        },
                      }}
                    >
                      <Menu.Target>
                        <Group
                          gap={6}
                          align="center"
                          wrap="nowrap"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            cursor: 'pointer',
                            marginTop: '6px',
                            padding: '5px 12px',
                            borderRadius: '10px',
                            border: '1.5px solid #1e293b',
                            backgroundColor: '#ebe8e3',
                            transition: 'background-color 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ddd8d1';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ebe8e3';
                          }}
                        >
                          <IconClock size={14} style={{ color: '#2d2a26' }} />
                          <Text size="xs" fw={600} style={{ color: '#2d2a26' }}>
                            Examen en progreso
                          </Text>
                          <IconDotsVertical size={14} style={{ color: '#2d2a26' }} />
                        </Group>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Label>
                          Pregunta {examenEnProgreso.preguntaActual + 1} de {examenEnProgreso.totalPreguntas} · {Math.round(((examenEnProgreso.preguntaActual + 1) / examenEnProgreso.totalPreguntas) * 100)}% · {formatearTiempo(examenEnProgreso.tiempoTranscurrido)}
                        </Menu.Label>
                        <Menu.Item
                          leftSection={<IconArrowRight size={14} />}
                          onClick={() => continuarExamen(examenEnProgreso!)}
                        >
                          Continuar examen
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconPlus size={14} />}
                          disabled={procesandoAccion}
                          onClick={() => empezarNuevoExamen(opcion, examenEnProgreso!)}
                        >
                          Empezar uno nuevo
                        </Menu.Item>
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          disabled={procesandoAccion}
                          onClick={() => eliminarExamenEnProgreso(opcion.id, examenEnProgreso!)}
                        >
                          Eliminar
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Stack>
              </Stack>

              {/* Badge "PREMIUM" con efecto dorado metalico */}
              {opcion.requiresPremium && esUsuarioGratuito === true && (
                <Box
                  style={{
                    position: 'absolute',
                    top: '0.75rem',
                    right: '0.75rem',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    style={{
                      background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
                      padding: '5px 12px',
                      borderRadius: '6px',
                      border: '1.5px solid rgba(251, 191, 36, 0.6)',
                      boxShadow: `
                        0 3px 8px rgba(251, 191, 36, 0.4),
                        0 1px 3px rgba(217, 119, 6, 0.3),
                        inset 0 1px 0 rgba(255, 255, 255, 0.25),
                        inset 0 -1px 0 rgba(0, 0, 0, 0.15)
                      `,
                      backdropFilter: 'blur(8px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(8px) saturate(180%)',
                      position: 'relative',
                      overflow: 'visible',
                    }}
                  >
                    <Text
                      style={{
                        color: '#ffffff',
                        fontFamily: 'Inter, sans-serif',
                        textTransform: 'uppercase',
                        letterSpacing: '0.9px',
                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
                        fontSize: '10px',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      PREMIUM
                    </Text>
                  </Box>
                </Box>
              )}
            </Paper>

            </Stack>
          );
        })}
      </SimpleGrid>


      {/* Loader mientras carga */}
      {cargandoProgreso && (
        <Box mb="3rem" ta="center">
          <Loader size="sm" />
        </Box>
      )}

      </Box>
    </>
  );
};

export default SimuladorPage;
